import type { CSSProperties } from 'react'
import { PRINT_BLEED_TINT_INSIDE_TRIM_PT } from '@/lib/document/print-bleed'
import { computeHalfTrimWidth } from '../../helpers/page-trim-width'
import {
  GAP_WARNING,
  INNER_YELLOW_TRIM_COPY,
  OUTER_YELLOW_BLEED_COPY,
} from '../constants/bleed-copy'
import type { BleedMetrics } from '../types/bleed-metrics'

export type BleedLabelSpec = {
  key: string
  /** `nowrap` keeps a label on one line; `normal` lets it wrap within its `maxWidth`. */
  wrap: 'nowrap' | 'normal'
  style: CSSProperties
  copy: string
}

/**
 * Every warning label `PrintBleedTintOverlay` draws over the tint ring, in paint order: outer and
 * in-trim copy along the top and bottom of each visible page half, then the rotated side copy
 * (outer, single-page spine gap warning, in-trim) when there is room for it. Empty when the page
 * halves have no width.
 */
export function computeBleedLabelLayout({
  bleedWidth,
  bleedHeight,
  bleedMargin,
  trimWidth,
  trimHeight,
  isSinglePage,
  singlePageSide,
}: BleedMetrics & {
  isSinglePage: boolean
  singlePageSide?: 'left' | 'right'
}): BleedLabelSpec[] {
  const showLeftPage = !isSinglePage || singlePageSide === 'left'
  const showRightPage = !isSinglePage || singlePageSide === 'right'
  const pageTrimWidth = computeHalfTrimWidth(trimWidth)
  const leftPageCenterX = bleedMargin + pageTrimWidth / 2
  const rightPageCenterX = bleedMargin + pageTrimWidth + pageTrimWidth / 2
  /** Center of outer top yellow (above dashed trim). */
  const outerTopLabelY = bleedMargin / 2
  /** Center of outer bottom yellow (below dashed trim). */
  const outerBottomLabelY = bleedHeight - bleedMargin / 2
  /** Just below the top dashed trim, inside the inner yellow (toward the page). */
  const topTrimLabelY = bleedMargin + 6
  /** Just above the bottom dashed trim, inside the inner yellow. */
  const bottomTrimLabelY = bleedMargin + trimHeight - 6
  const labelZInner = 51
  const labelZOuter = 49
  const insideTint = PRINT_BLEED_TINT_INSIDE_TRIM_PT
  const bandTotal = bleedMargin + insideTint
  const sideH = bleedHeight - bandTotal * 2
  /** Along-page length available for rotated side copy (between top/bottom bleed bands). */
  const sideBleedLabelMaxWidth = Math.max(48, sideH - 16)
  const outerSideLabelY = bleedHeight / 2
  const leftOuterSideX = bleedMargin / 2
  const rightOuterSideX = bleedWidth - bleedMargin / 2
  /** Inner yellow column (between trim and page) — not the paper margin strip (`outsideInset`). */
  const leftInnerSideX = bleedMargin + insideTint / 2
  const rightInnerSideX = bleedWidth - bleedMargin - insideTint / 2
  /** Stops shrink-to-fit from squeezing right-half labels; keeps one line like the left page. */
  const horizontalBleedLabelWidth = Math.max(48, pageTrimWidth - 16)
  /** Matches `double-spread-document.tsx` center spine line / gutter warning anchor. */
  const spineX = bleedMargin + pageTrimWidth
  const singlePageSpineOuterLabelX =
    isSinglePage && singlePageSide === 'right'
      ? spineX - bleedMargin / 2
      : isSinglePage && singlePageSide === 'left'
        ? spineX + bleedMargin / 2
        : 0

  if (pageTrimWidth <= 0) {
    return []
  }

  const labels: BleedLabelSpec[] = []
  const pages = [
    { side: 'left', show: showLeftPage, centerX: leftPageCenterX },
    { side: 'right', show: showRightPage, centerX: rightPageCenterX },
  ] as const

  const outerRow = (row: string, top: number) => {
    for (const page of pages) {
      if (!page.show) continue
      labels.push({
        key: `${row}-${page.side}`,
        wrap: 'nowrap',
        style: {
          left: page.centerX,
          top,
          transform: 'translate(-50%, -50%)',
          width: horizontalBleedLabelWidth,
          maxWidth: horizontalBleedLabelWidth,
          zIndex: labelZOuter,
        },
        copy: OUTER_YELLOW_BLEED_COPY,
      })
    }
  }

  const innerRow = (row: string, top: number, transform: string) => {
    for (const page of pages) {
      if (!page.show) continue
      labels.push({
        key: `${row}-${page.side}`,
        wrap: 'normal',
        style: {
          left: page.centerX,
          top,
          transform,
          maxWidth: pageTrimWidth - 16,
          zIndex: labelZInner,
        },
        copy: INNER_YELLOW_TRIM_COPY,
      })
    }
  }

  const sideLabel = (
    key: string,
    left: number,
    rotate: string,
    zIndex: number,
    copy: string
  ): BleedLabelSpec => ({
    key,
    wrap: 'normal',
    style: {
      left,
      top: outerSideLabelY,
      transform: `translate(-50%, -50%) rotate(${rotate})`,
      width: sideBleedLabelMaxWidth,
      maxWidth: sideBleedLabelMaxWidth,
      zIndex,
    },
    copy,
  })

  outerRow('outer-top', outerTopLabelY + 2)
  innerRow('inner-top', topTrimLabelY, 'translate(-50%, 0)')
  innerRow('inner-bottom', bottomTrimLabelY, 'translate(-50%, -100%)')
  outerRow('outer-bottom', outerBottomLabelY)

  if (bleedMargin > 0 && sideH >= 24) {
    if (showLeftPage) {
      labels.push(
        sideLabel('outer-side-left', leftOuterSideX, '-90deg', labelZOuter, OUTER_YELLOW_BLEED_COPY)
      )
    }

    if (showRightPage) {
      labels.push(
        sideLabel(
          'outer-side-right',
          rightOuterSideX,
          '90deg',
          labelZOuter,
          OUTER_YELLOW_BLEED_COPY
        )
      )
    }

    if (isSinglePage && singlePageSpineOuterLabelX > 0 && singlePageSide) {
      labels.push({
        key: 'outer-side-spine',
        wrap: 'nowrap',
        style: {
          left: singlePageSpineOuterLabelX,
          top: outerSideLabelY,
          transform: `translate(-50%, -50%) rotate(${singlePageSide === 'left' ? '90deg' : '-90deg'})`,
          maxWidth: sideBleedLabelMaxWidth,
          zIndex: labelZOuter,
          fontSize: 8,
          lineHeight: '10px',
        },
        copy: GAP_WARNING,
      })
    }

    if (showLeftPage) {
      labels.push(
        sideLabel('inner-side-left', leftInnerSideX, '-90deg', labelZInner, INNER_YELLOW_TRIM_COPY)
      )
    }

    if (showRightPage) {
      labels.push(
        sideLabel('inner-side-right', rightInnerSideX, '90deg', labelZInner, INNER_YELLOW_TRIM_COPY)
      )
    }

    if (isSinglePage) {
      labels.push(
        sideLabel(
          'inner-side-spine',
          singlePageSide === 'left' ? spineX - insideTint / 2 : spineX + insideTint / 2,
          singlePageSide === 'left' ? '90deg' : '-90deg',
          labelZInner,
          INNER_YELLOW_TRIM_COPY
        )
      )
    }
  }

  return labels
}
